'use client'
import '@aws-amplify/ui-react/styles.css'
import '@/styles/globals.css'
import '@/styles/style.css'
import { Header, Providers, SideMenu } from '@/components'
import { CssBaseline, Stack } from '@mui/material'
import { Amplify } from 'aws-amplify'
import { I18n } from 'aws-amplify/utils'
import { translations } from '@aws-amplify/ui'
import { jaDict } from '@/utils/vocabularies'
import {
  Authenticator,
  Flex,
  Image,
  Link,
  Text,
  useAuthenticator,
  useTheme,
} from '@aws-amplify/ui-react'
import { Box, Typography } from '@mui/material'
import { useRouter } from 'next/navigation'
import { useEffect, useReducer } from 'react'
import { PATH_HOME } from '@/utils/pageRoutes'

// ログイン画面 日本語化
I18n.putVocabularies(translations) // ライブラリ標準の翻訳
I18n.putVocabularies(jaDict) // カスタムの翻訳
I18n.setLanguage('ja') // 言語設定

// Auth config
const authConf = {
  Auth: {
    Cognito: {
      userPoolId: process.env.NEXT_PUBLIC_COGNITO_USERPOOL_ID,
      userPoolClientId: process.env.NEXT_PUBLIC_COGNITO_USERPOOL_WEB_CLIENT_ID,
    },
  },
}
Amplify.configure(authConf)

// Authenticator UI custom
const AuthHeader = () => {
  return (
    <Flex backgroundColor='#001e3c' justifyContent='center' padding='2rem'>
      <Image src='/logo.png' alt='logo' loading='lazy' />
    </Flex>
  )
}

const AuthFooter = () => {
  return (
    <Flex justifyContent='center'>
      &copy; All Rights Reserved, Osaka Digital Exchange Co.,Ltd.
    </Flex>
  )
}

const AuthSignInHeader = () => {
  return (
    <Flex justifyContent='center'>
      <Typography variant='h6'>
        {process.env.NEXT_PUBLIC_SYSTEM_NAME}
      </Typography>
    </Flex>
  )
}

const AuthSignInFooter = () => {
  const { toForgotPassword } = useAuthenticator()
  const { tokens } = useTheme()

  return (
    <Flex justifyContent='center' padding={`0 0 ${tokens.space.medium}`}>
      <Link onClick={toForgotPassword}>
        パスワードを忘れた場合、もしくは再設定する場合
      </Link>
    </Flex>
  )
}

const AuthConfirmResetPasswordFooter = () => {
  const { toSignIn } = useAuthenticator()
  const { tokens } = useTheme()

  return (
    <>
      <Flex justifyContent='center' padding={`0 0 ${tokens.space.medium}`}>
        <Link onClick={toSignIn} style={{ fontSize: '14px' }}>
          サインインに戻る
        </Link>
      </Flex>
      <Text whiteSpace={'pre-line'}>
        {`※1. パスワードは${process.env.NEXT_PUBLIC_COGNITO_USERPOOL_PASSWORD_MIN_LENGTH}文字以上、英小文字、英大文字、数字、記号をそれぞれ1つ以上使用してください。使用できる記号は以下です。
    ^ $ * . [ ] { } ( ) ? - " ! @ # % & /  , > < ' : ; | _ ~ \` + =`}
      </Text>
      <Text>
        ※2.
        数分経過してもメールが届かない場合は、サインイン画面へ戻り、再入力をお試しください
      </Text>
    </>
  )
}

const components = {
  Header: AuthHeader,
  SignIn: {
    Header: AuthSignInHeader,
    Footer: AuthSignInFooter,
  },
  Footer: AuthFooter,
  ConfirmResetPassword: {
    Footer: AuthConfirmResetPasswordFooter,
  },
}

const VerifyAuthStatus = () => {
  const router = useRouter()
  const { authStatus } = useAuthenticator((context) => [context.authStatus])

  useEffect(() => {
    if (authStatus === 'unauthenticated') {
      router.replace(PATH_HOME)
    }
  }, [authStatus])

  return <></>
}

export default function RootLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const [toggle, setToggle] = useReducer((prev) => !prev, true)
  return (
    <html lang='ja'>
      <head>
        <title>{process.env.NEXT_PUBLIC_SYSTEM_NAME}</title>
        <meta name='viewport' content='width=device-width, initial-scale=1' />
        {/* <link rel='icon' href='/favicon.ico' /> */}
        <link rel='icon' href='/images/favicon.ico' />
      </head>
      <Providers>
        <CssBaseline />
        <body>
          <Authenticator.Provider>
            <VerifyAuthStatus />
            <Authenticator
              components={components}
              loginMechanisms={[
                process.env.NEXT_PUBLIC_COGNITO_LOGIN_MECHANISM as
                  | 'username'
                  | 'email',
              ]}
              hideSignUp
            >
              {() => {
                return (
                  <Stack direction='row' height='100vh'>
                    <SideMenu {...{ toggle }} />
                    <Stack width={toggle ? 'calc(100vw - 250px)' : '100vw'}>
                      <Header {...{ toggle, setToggle }} />
                      <Box
                        sx={{
                          px: 4.5,
                          py: 5,
                          flexGrow: 1,
                          backgroundColor: '#f5f5f5',
                          overflowY: 'scroll',
                        }}
                      >
                        {children}
                      </Box>
                    </Stack>
                  </Stack>
                )
              }}
            </Authenticator>
          </Authenticator.Provider>
        </body>
      </Providers>
    </html>
  )
}
